// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { TraitValueModel, VariantTraitModel } from "@insite/client-framework/Types/ApiModels";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
// import Swatch, { SwatchTypeValues } from "@insite/mobius/Swatch"; // Starter Custom Code
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import styled, { css } from "styled-components";

/* Starter Custom Code - Start */
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
// import Swatch, { SwatchTypeValues } from "@store/MobiusOverrides/Swatch/Swatch"; // Store Custom Code
import { linkActionStyles } from "@store/Themes/common/mixins/link";
import { swatchListItemStyles, swatchListStyles } from "@store/Themes/common/mixins/swatch";
// import { productCardProps } from "@store/Themes/common/props/productCard"; // Store Custom Code
// import { swatchProps } from "@store/Themes/common/props/swatch"; // Store Custom Code
import useSwatchSelectedAccessibleText from "@toolkit/Framework/Common/Hooks/useSwatchSelectedAccessibleText";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import getTrimTraitValueDisplayName from "@store/Framework/Common/Utilities/getTrimTraitValueDisplayName";
import Swatch, { SwatchTypeValues } from "@store/MobiusOverrides/Swatch/Swatch";
import { productCardProps } from "@store/Themes/common/props/productCard";
import { swatchProps } from "@store/Themes/common/props/swatch";
/* Store Custom Code - End */

interface Props {
    product: ProductContextModel;
    variantTrait: VariantTraitModel;
}

export interface ProductListVariantSwatchStyles {
    container?: InjectableCss;
    swatchClickable?: ClickablePresentationProps;
    labelText?: TypographyPresentationProps;
    labelWrapper?: InjectableCss;
    productDetailLink?: LinkPresentationProps;
    // Store Custom Code
    selection?: TypographyPresentationProps;
}

export const productListVariantSwatchStyles: ProductListVariantSwatchStyles = {
    container: productCardProps.swatchWrapper, // Store Custom Code
    swatchClickable: swatchProps.clickable,
    labelText: {
        css: css`
            display: block;
            margin-bottom: ${x => x.theme.design.product.list.contentSpacing /* Store Custom Code */};
            font-size: ${x => x.theme.design.typography.size.smaller /* Store Custom Code */};
            font-weight: ${x => x.theme.design.typography.weight.base /* Store Custom Code */};
        `,
    },
    selection: swatchProps.selection, // Store Custom Code
};

const SwatchGridDisplayItem = styled.li`
    ${swatchListItemStyles() /* Starter Custom Code */}
`;

const SwatchGridDisplayViewMore = styled.li`
    ${swatchListItemStyles() /* Starter Custom Code */}
    ${linkActionStyles() /* Starter Custom Code */}
`;

export const UnorderedList = styled.ul<InjectableCss>`
    ${swatchListStyles() /* Starter Custom Code */}
    ${injectCss}
`;

const styles = productListVariantSwatchStyles;

const ProductListVariantSwatch = ({ product, variantTrait }: Props) => {
    // Store Removed - swatchSelectedAccessibleText
    /* Store Custom Code - Start */
    const productTraitValue = product.product.childTraitValues?.find(x => x.styleTraitId === variantTrait.id);
    const filteredTraitValues = variantTrait.traitValues?.filter(x => !x.isDisabled && !x.isUnavailable);
    const selection = filteredTraitValues?.find(x => x.id === productTraitValue?.id);

    if (selection && filteredTraitValues) {
        // Move the selection to the begining of the list regardless of sort order.
        filteredTraitValues.unshift(
            filteredTraitValues.splice(
                filteredTraitValues.findIndex(x => x.id === selection.id),
                1,
            )[0],
        );
    }

    const selectionDisplayName = selection
        ? getTrimTraitValueDisplayName(product.product, variantTrait.name, selection.valueDisplay)
        : "";
    /* Store Custom Code - End */

    const CreateSwatchGrid = (traitValue: TraitValueModel) => {
        /* Store Custom Code - Start */
        const isSelected = selection?.id === traitValue.id;

        type ClickableComponentProps = {
            children: React.ReactNode;
        };

        const ClickableComponent = ({ children }: ClickableComponentProps) => {
            if (!product.productInfo.productDetailPath) {
                return <>{children}</>;
            }

            return (
                <Clickable
                    {...styles.swatchClickable}
                    href={`${product.productInfo.productDetailPath}${!isSelected ? `?t=${variantTrait.id}&v=${traitValue.id}` : ""}`}
                    data-selected={isSelected || undefined}
                >
                    {children}
                </Clickable>
            );
        };
        /* Store Custom Code - End */

        return (
            <SwatchGridDisplayItem key={traitValue.id} title={traitValue.valueDisplay /* Starter Custom Code */}>
                <ClickableComponent /* Store Custom Code */>
                    <Swatch
                        type={traitValue.swatchType as SwatchTypeValues}
                        value={
                            traitValue.swatchType === SwatchTypeValues.Image
                                ? traitValue.swatchImageValue
                                : traitValue.swatchColorValue
                        }
                        size={26} // Store Custom Code
                        isDisabled={traitValue.isDisabled}
                        isSelected={isSelected} // Store Custom Code
                        // Starter Custom Code
                        caption={traitValue.valueDisplay}
                        hideCaption
                        // Store Custom Code
                        isUnavailable={traitValue.isUnavailable}
                    />
                </ClickableComponent>
            </SwatchGridDisplayItem>
        );
    };

    const ViewMoreLink = (product: ProductContextModel) => {
        const productDetailPath = product.productInfo.productDetailPath;

        /* Store Custom Code - Start */
        const valueCount = filteredTraitValues?.length;
        if (!valueCount) {
            return;
        }
        /* Store Custom Code - End */

        return (
            <SwatchGridDisplayViewMore data-test-selector="swatch_viewMore">
                <Link
                    {...styles.productDetailLink}
                    href={productDetailPath}
                    // Starter Removed - alt
                    data-test-selector="swatch_ProductDescriptionLink"
                >
                    {/* Store Custom Code - Start */}
                    {translate("+ {0} more", `${valueCount - variantTrait.numberOfSwatchesVisible}`)}
                    {/* Store Custom Code - End */}
                    {/* Starter Custom Code - Start */}
                    <VisuallyHidden>{variantTrait.nameDisplay}</VisuallyHidden>
                    {/* Starter Custom Code - End */}
                </Link>
            </SwatchGridDisplayViewMore>
        );
    };

    return (
        <StyledWrapper
            key={variantTrait.id}
            data-test-selector="swatch_productListProductSwatchList"
            {...styles.container}
        >
            <StyledWrapper {...styles.labelWrapper}>
                <Typography {...styles.labelText}>
                    {/* Store Custom Code - Start */}
                    {translate("{0}:", variantTrait.nameDisplay)}
                    {Boolean(selectionDisplayName) && (
                        <>
                            <Typography {...styles.selection}>&nbsp;{selectionDisplayName}</Typography>
                            <VisuallyHidden>&nbsp;{translate("Selected")}</VisuallyHidden>
                        </>
                    )}
                    {/* Store Custom Code - End */}
                </Typography>
            </StyledWrapper>
            <UnorderedList>
                {filteredTraitValues // Store Custom Code
                    ?.slice(0, variantTrait.numberOfSwatchesVisible)
                    .map(traitValue => CreateSwatchGrid(traitValue))}
                {variantTrait &&
                    filteredTraitValues && // Store Custom Code
                    variantTrait.numberOfSwatchesVisible < filteredTraitValues?.length && // Store Custom Code
                    ViewMoreLink(product)}
            </UnorderedList>
        </StyledWrapper>
    );
};

export default ProductListVariantSwatch;
